import { type FormEvent } from 'react';
import clsx from 'clsx';
import MailIcon from '@assets/icons/mail.svg?react';
import SafeIcon from '@assets/icons/safe.svg?react';
import { Eye, EyeOff } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@ui/button';
import { Input } from '@ui/input';
import { routesMap } from '@shared/lib';

import styles from './login.module.css';

type LoginProps = {
  email: string;
  password: string;
  error: string;
  isPasswordVisible: boolean;
  isSubmitting: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPasswordVisibilityToggle: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
};

export const Login = ({
  email,
  password,
  error,
  isPasswordVisible,
  isSubmitting,
  onEmailChange,
  onPasswordChange,
  onPasswordVisibilityToggle,
  onSubmit,
}: LoginProps) => {
  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <div className={styles.fields}>
        <label>
          <span className={styles.label}>Email</span>
          <span className={styles.field}>
            <MailIcon className={styles.icon} aria-hidden />
            <Input
              className={clsx(styles.input, styles.withIcon)}
              name="email"
              type="email"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              placeholder="exampleemail@email.com"
              autoComplete="email"
              inputSize="lg"
            />
          </span>
        </label>
        <label>
          <span className={styles.label}>Password</span>
          <span className={styles.field}>
            <SafeIcon className={styles.icon} aria-hidden />
            <Input
              className={clsx(styles.input, styles.withIcon)}
              name="password"
              type={isPasswordVisible ? 'text' : 'password'}
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              inputSize="lg"
            />
            <button
              className={styles.passwordButton}
              type="button"
              onClick={onPasswordVisibilityToggle}
              aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
            >
              {isPasswordVisible ? (
                <EyeOff className={styles.passwordIcon} />
              ) : (
                <Eye className={styles.passwordIcon} />
              )}
            </button>
          </span>
        </label>
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button className={styles.button} type="submit" disabled={isSubmitting}>
          Sign in
        </Button>
        <Link className={styles.link} to={routesMap.authPasswordReset}>
          Forgot Password?
        </Link>
      </div>
    </form>
  );
};
